import { Bell, X } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { Separator } from '@/shared/ui/separator';
import { SidebarMenuButton } from '@/shared/ui/sidebar';
import { cn } from '@/shared/lib/cn';
import { useNotificationsStore } from '@/features/notifications';
import { useSlackNotificationSource } from '@/features/notifications';
import type { Notification } from '@/features/notifications';

const dotColor: Record<string, string> = {
  info: 'bg-primary',
  notice: 'bg-status-warning',
  negative: 'bg-status-error',
  positive: 'bg-status-success',
};

function NotificationItem({ n }: { n: Notification }) {
  const markRead = useNotificationsStore((s) => s.markRead);
  const dismiss = useNotificationsStore((s) => s.dismiss);

  return (
    <div className="flex flex-col gap-1.5 px-4 py-3">
      <div className="flex items-center gap-2">
        <span aria-label={n.severity} className={cn('size-2 shrink-0 rounded-full', dotColor[n.severity] ?? 'bg-muted-foreground')} />
        <span className="flex-1 font-bold">{n.title}</span>
        {n.dismissible && (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Dismiss notification"
            onClick={() => dismiss(n.id)}
          >
            <X />
          </Button>
        )}
      </div>
      {n.description != null && <span className="text-xs text-muted-foreground">{n.description}</span>}
      {n.action != null && (
        <div>
          <Button
            size="sm"
            onClick={() => {
              n.action!.onAction();
              markRead(n.id);
            }}
          >
            {n.action.label}
          </Button>
        </div>
      )}
    </div>
  );
}

export function NotificationsWidget() {
  // Mount the Slack notification source here so it is always active when the
  // shell is rendered, without requiring AppShell to import from this feature.
  useSlackNotificationSource();

  const list = useNotificationsStore((s) => s.list);
  const unreadCount = useNotificationsStore((s) => s.list.filter((n) => !n.read).length);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);

  return (
    <Popover
      onOpenChange={(open) => {
        if (open) markAllRead();
      }}
    >
      <PopoverTrigger asChild>
        <SidebarMenuButton
          tooltip="Notifications"
          aria-label="Notifications"
          className="relative px-2.5 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[active=true]:font-semibold group-data-[collapsible=icon]:!size-7 group-data-[collapsible=icon]:justify-center"
        >
          <Bell />
          <span className="group-data-[collapsible=icon]:hidden">Notifications</span>
          {unreadCount > 0 ? (
            <div className="ml-auto group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:right-0 group-data-[collapsible=icon]:top-0">
              <Badge
                aria-label={`${unreadCount} unread notifications`}
                className="min-w-5 justify-center rounded-full px-1.5 text-xs group-data-[collapsible=icon]:min-w-4 group-data-[collapsible=icon]:px-1"
              >
                {unreadCount}
              </Badge>
            </div>
          ) : null}
        </SidebarMenuButton>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="right"
        sideOffset={12}
        className="w-88 overflow-hidden rounded-xl border border-border bg-popover p-0 shadow-lg"
      >
        <div className="flex items-center justify-between border-b border-border bg-muted/40 px-4 py-3">
          <div className="flex min-w-0 flex-col">
            <span className="text-sm font-semibold text-foreground">Notifications</span>
            <span className="text-xs text-muted-foreground">Recent updates and actions</span>
          </div>
          {unreadCount > 0 && (
            <Button variant="secondary" size="sm" onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </div>
        {list.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <span className="text-sm text-muted-foreground">{"You're all caught up"}</span>
          </div>
        ) : (
          <div className="max-h-100 overflow-y-auto">
            {list.map((n, i) => (
              <div key={n.id}>
                <NotificationItem n={n} />
                {i < list.length - 1 && <Separator />}
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
